import { IGeorefArguments } from "../georef/types"
import { FieldViewModel, convertGeometryToWkt, Datalayer, DatalayersMap, DatalayerZone, defineComponent, handleApiError, ICrop, IDataLayer, IDatalayerContext, IDatalayerEditModel, IDataLayerType, IDocumentLibraryService, IHolding, IOmniaShell, IParam, isTerraMapLayer, isUndefinedOrNull, LayerEditMode, LayerEditorConfig, LayerTypeGroupId, LayerTypeId, LayerViewModel, LocaleString, omniaResolve, setupVariants, shell, str, param } from "../omnia"
import { IDatalayerMapDependencies, IDatalayersService } from "../types"
import { canChangeLayerCropping, canDeleteLayer } from "../utils"
import { getLayerActivity } from "./activity"
import { EditLayerCmd } from "./editCmd"
import { EditContext } from "./editContext"
import { GenericZoneEditor } from "./generic/editor"
import { SplittingZoneEditor } from "./splits/editor"
import { IGenericPlanContext, ILayerCreateContext, ILayerValidationResults, IZoneEditor } from "./types"
import { getUpdateModel } from "./update"
import { getAllLayers, getLayerTypeOrder } from "./utils"

import m = omnia.models;
import { ol_VectorLayer, ol_Feature, ol_source, ol_style, ol_geom, ol_layer } from "@omnia/exports/openlayers"

export class EditLayerCtrl extends omnia.shell.Component {

    static create(): ng.IDirective {

        return defineComponent(EditLayerCtrl, "app/datalayers/edit/edit.html", {
            map: "="
        });

    };

    protected map: DatalayersMap;

    protected allLayers: LayerViewModel[];
    protected visibleLayers: LayerViewModel[];
    protected sourceLayers: LayerViewModel[];

    creationMode: number = 1
    resetMode: boolean

    config: LayerEditorConfig

    layerTypes: IDataLayerType[];
    layerUnits: IParam[];

    layerSubtypes: IParam[];
    subtypeLabel: string;

    protected _showButtons: boolean = true;
    protected _canEditFeatures: boolean = true;

    createContext: ILayerCreateContext
    editContext: EditContext

    target: IDataLayer;

    headerForm: ng.IFormController;
    showValidationErrors: boolean;
    validationResults: ILayerValidationResults;

    holding: IHolding;
    targetField: FieldViewModel;
    _targetCropping: FieldViewModel;
    targetCroppingList: FieldViewModel[];

    private _outerGeometry: ol_geom.Geometry;
    private _outerGeometryLayer: ol_VectorLayer;

    layer: Datalayer;
    private zoneEditor: IZoneEditor;

    private _opacity: number;
    private _opacityRefresh: ng.IPromise<any>;

    public editZoneTitle = "Edit management zones" as LocaleString

    public genericPlanContext: IGenericPlanContext
    private _planWatcher: Function

    static $inject = [
        "$scope",
        "$parse",
        "OmniaShell",
        "OmniaDatalayersService",
        "OmniaDatalayersDependencies",
        "OmniaDocumentLibraryService"
    ];


    constructor(
        $scope: ng.IScope,
        private $parse: ng.IParseService,
        protected readonly shell: IOmniaShell,
        private readonly svc: IDatalayersService,
        private readonly dependencies: IDatalayerMapDependencies,
        private readonly documentSvc: IDocumentLibraryService,
        private readonly viewManager = dependencies.viewManager,
        private readonly layerFactory = dependencies.factory,
        private readonly readOnly = shell.isReadOnly(p => p.mapLayers)) {

        super($scope, ["vm.map"]);

        this._icon = "fas fa-pen";

    }

    get title(): string {
        const msgCreateNewLayer = 'Create a new layer';
        const msgEditLayer = 'Edit a layer';
        const { config, newLayer } = this
        return config.title ?? (newLayer ? msgCreateNewLayer : msgEditLayer)
    }

    $onInit() {

        const { map, componentContext: ctxt } = this

        if (ctxt && map) {

            const cmd = ctxt.execArguments;

            this.setupEditContext(cmd, map, true)

            if (this.isPictureLayer) {
                this.startEditingPicture()
                return
            }

            if (cmd.layerType && this.newLayer) {
                this.layerType = cmd.layerType
            }

        }

    }

    protected setupEditContext(cmd: EditLayerCmd, map: DatalayersMap, canEditFeatures: boolean) {

        const { svc } = this
        const editContext = this.editContext = new EditContext(this.$parse, svc, cmd, map);

        const config = this.config = editContext.config

        map.manageCustomColors(false)

        this.target = editContext.target;
        this.holding = editContext.holding;
        this.targetField = editContext.targetField;
        this.targetCropping = editContext.targetCropping;
        this.targetCroppingList = editContext.targetCroppingList;

        this.setupAvailableLayers(cmd.availableLayers);

        if (this.isCroppingLayer && this.targetCropping) {
            map.fieldsLayer.clearHighlight()
            map.croppingLayer.highlight([this.targetCropping]);
        }
        else if (!this.isHoldingLayer) {
            map.fieldsLayer.highlight([this.targetField]);
            map.croppingLayer.highlight(this.targetField.croppings);
        }

        this._outerGeometry = this.editContext.outerGeometry
        this.setupAvailableLayerTypes()

        this._canEditFeatures = canEditFeatures && (config.canEditFeatures ?? true)

        this.setupLayers(true)

        this.setupUnits()
        this.setupSubtypes()

        if (this.editContext.isHoldingLayer && this.newLayer) {
            this.layerType = this.layerTypes[0];
        }

        this.updateCreateContext()

    }

    protected updateCreateContext() {

        this.createContext = !this.layer
            ? null
            : {
                editContext: this.editContext,
                resetMode: this.resetMode,
                layer: this.layer,
                create: () => this.startEditingNewLayer()
            }

    }

    protected setupLayerEditor(layerContext: IDatalayerContext) {

        if (this.layerType.GenericFeatures) {
            this.zoneEditor = new GenericZoneEditor(this.$scope, this.map, this.dependencies, this._outerGeometry, this.documentSvc)
        }
        else {
            this.zoneEditor = new SplittingZoneEditor(this.$scope, this.svc, this.map, this.dependencies, () => this._outerGeometry, z => this.onZonesChanged(z), () => this.genericPlanContext)
        }

        this.zoneEditor.setLayerTargets(this.targetField, this._targetCropping, this.targetCroppingList)

        this.layer = this.zoneEditor.setup(this.target, layerContext)
        this.layer.editMode = LayerEditMode.EditTarget

        if (this.layer.datalayerId) {
            this.map.removePictureOverlaysForLayerId(this.layer.datalayerId)
        }

        setupVariants([this.layer], this.layerType)

        this.updateCreateContext()

        if (!this.newLayer) {
            this.zoneEditor.startEditing()
        }

        this.opacity = 80

        this.setActivity(getLayerActivity(this.newLayer, this.layer))

    }

    protected cleanupEditor() {
        this.map.manageCustomColors(true)
        this.map.contextLayers = null
    }

    setupAvailableLayerTypes() {

        const { config, targetField } = this

        if (config.availableLayerTypes) {
            this.layerTypes = config.availableLayerTypes
            return
        }

        this.layerTypes = this.svc.getLayerTypes()
            .filter(lt => !!lt.Standalone)
            .filter(lt => lt.GroupId != LayerTypeGroupId.TerraMap)
            .filter(lt => (lt.TypeId !== LayerTypeId.GenericPlan) || hasAuthorizedCroppingOnField(targetField))
            .sort((l1, l2) => {

                let diff = getLayerTypeOrder(l1) - getLayerTypeOrder(l2);

                if (!diff) {
                    diff = l1.Name.localeCompare(l2.Name);
                }

                return diff;

            });

        if (this.isHoldingLayer) {
            this.layerTypes = this.layerTypes.filter(l => l.TypeId === LayerTypeId.Drawing)
        }

    }

    setupAvailableLayers(availableLayers: LayerViewModel[]) {

        const { target, targetField, isHoldingLayer } = this
        const id = target.DataLayerId

        this.allLayers = getAllLayers(availableLayers).filter(l => l.datalayerId != id)

        this.sourceLayers = this.allLayers.filter(l =>
                isHoldingLayer
                    ? (!!l.holdingId && !l.fieldId)
                    : (l.fieldId == targetField?.fieldId))

        this.map.contextLayers = this.sourceLayers

        this.visibleLayers = this.allLayers.filter(l => l.visible)

    }

    setupSubtypes() {

        this.layerSubtypes = null;

        let type = this.layerType;

        if (type) {

            switch (type.TypeId) {

                case LayerTypeId.Yield:
                    this.layerSubtypes = param.YieldLayerSubtype();
                    this.subtypeLabel = "Yield type" as LocaleString;
                    break;

            }

        }

    }

    setupUnits() {

        this.layerUnits = null;

        let type = this.layerType;

        if (type) {

            switch (type.TypeId) {

                case LayerTypeId.Biomass:
                    this.layerUnits = param.BiomassUnit();
                    break;

            }

        }

    }

    get headerTemplateUrl(): string {
        const { config: options } = this
        return options.headerTemplateUrl ?? "app/datalayers/edit/header.html";
    }

    get footerTemplateUrl() {
        return this.isGenericPlanLayer
            ? "app/datalayers/edit/genplan/footer.html"
            : undefined
    }

    get fields(): FieldViewModel[] {
        return this.svc.fields;
    }

    get isHoldingLayer() {
        return this.editContext && this.editContext.isHoldingLayer;
    }

    get layerType(): IDataLayerType {
        return this.target && this.target.Type;
    }

    set layerType(value: IDataLayerType) {
        this.target.TypeId = (value ? value.TypeId : null)
        this.target.Type = value
        this.setupLayers(false)
        this.setupUnits()
        this.setupSubtypes()
        this.checkCreationMode()
    }

    get targetCropping() {
        return this._targetCropping
    }

    set targetCropping(value) {

        this._targetCropping = value

        if (this.editContext) {
            this.editContext.targetCropping = value
            this._outerGeometry = this.editContext.outerGeometry
        }

        if (this.zoneEditor) {
            this.zoneEditor.setLayerTargets(this.targetField, this._targetCropping, this.targetCroppingList)
        }

    }

    get zones(): DatalayerZone[] {
        if (!this.layer) return [];
        return this.layer.zones;
    }

    get currentZone(): DatalayerZone {
        if (this.zoneEditor) {
            return this.zoneEditor.currentZone;
        }
    }

    get zone(): DatalayerZone {
        return this.currentZone
    }

    get newLayer(): boolean {
        return (!(this.target && this.target.DataLayerId));
    }

    canSetName() {
        const { layerType: type } = this
        return type
            ? !(type.LayerName?.NotAvailable)
            : false
    }

    get layerNameRequired() {
        const type = this.layerType
        return type && type.LayerName && type.LayerName.Required
    }

    checkLayerType(id: LayerTypeId) {
        const type = this.layerType
        return type && (type.TypeId == id)
    }

    get isPictureLayer() {
        return this.checkLayerType(LayerTypeId.Picture)
    }

    get isDrawingLayer() {
        return this.checkLayerType(LayerTypeId.Drawing)
    }

    get isGenericPlanLayer() {
        return this.checkLayerType(LayerTypeId.GenericPlan)
    }

    shouldSetupNewLayer() {
        if (this.resetMode) return true
        if (this.newLayer && this.editContext) {
            return !this.editContext.newLayerInit
        }
    }

    shouldShowButtons() {
        return this._showButtons;
    }

    private startEditingNewLayer(reset?: boolean) {

        if (this.layer) {
            if (reset) {
                this.layer.removeAllZones()
            }
            this.setResetMode(false)
        }

        if (this.isPictureLayer) {
            this.startEditingPicture()
        }
        else {
            this.editContext.startEditingNewLayer(this.layer, this.zoneEditor)
            this.zoneEditor.startEditing()
        }

    }

    private startEditingPicture() {

        let args: IGeorefArguments = {

            layerId: this.target ? this.target.DataLayerId : 0,

            georefPictureInfo: this.target ? this.target.GeorefPicture : null,

            field: {
                fieldId: this.targetField.fieldId,
                name: this.targetField.fieldName,
                boundaryWkt: convertGeometryToWkt(this.targetField.getBoundary()),
            },


        };

        this._showButtons = false;

        let p = this.shell.showComponentAsDialog("omnia-maplayers-edit-georef", args, {
            size: "full-screen",
        });

        p = p.then(id => {

            if (!id) {
                this.cancel();
            }

            if (id) {

                this.svc.refresh().then(() => {

                    if (id > 0) {
                        this.svc.setLayerVisibility(id, true);
                    }

                    this.cancel();
                });

            }

        })

        p = p.catch(() => this.cancel());


    }

    get isCroppingLayer() {
        return (this.target && this.target.Type && this.target.Type.Scope == m.MapScope.Cropping);
    }

    isCroppingOptional(): boolean {
        return this.isCroppingLayer && this.target.Type.CroppingOptional;
    }

    canSetCrop(): boolean {
        return (this.isCroppingOptional() && (!this.targetCropping));
    }

    get crops(): ICrop[] {
        return this.svc.crops;
    }

    getCroppingFilter() {

        return (c: FieldViewModel) => {

            const genericPlan = this.layerType?.TypeId === LayerTypeId.GenericPlan

            return (!genericPlan)
                || c.authorized
                || c.croppingId === this.targetCropping?.croppingId

        }

    }

    hasType(): boolean {
        const { target } = this
        return Boolean(target?.Type)
    }

    hasYear() {
        const type = this.target?.Type
        return type?.YearApplies && (!this.targetCropping);
    }

    canSetYear() {
        return this.hasYear() && (!isTerraMapLayer(this.target))
    }

    hasDate(): boolean {
        const type = this.target?.Type
        return isTerraMapLayer(type) || type?.DateApplies
    }

    canSetDate() {
        return this.hasDate() && (!isTerraMapLayer(this.target))
    }

    canChangeType(): boolean {
        const { layer, config } = this
        const canChange = config.canChangeLayerType ?? (!layer?.zones?.length)
        return canChange
    }

    canSelectCropping(): boolean {
        const type = this.target.Type
        if (type && (type.Scope == m.MapScope.Cropping)) {
            if (this.newLayer) return true
            if (!this.target) return false
            if (!this.target.FirstYearId) return true
            if (this.svc.yearId == this.layer.datalayer.FirstYearId) {
                return true
            }
        }
    }

    canChangeCropping() {
        return canChangeLayerCropping(this.target)
    }

    currentZoneAreaCanChange(): boolean {
        return this._canEditFeatures;
    }

    canEditLayerZones(): boolean {
        return !this.shouldSetupNewLayer()
    }

    checkCreationMode() {

        let reset = false

        switch (this.creationMode) {
            case 2:
                reset = !this.showCopyCmd()
                break
            case 3:
                reset = !this.showGridCreateCmd()
                break
        }

        if (reset) {
            this.creationMode = 1
        }

    }

    showCreationOptions(): boolean {

        if (!this.shouldSetupNewLayer()) {
            return false
        }

        const type = this.target.Type

        if (!type) {
            return false
        }

        if (this.isHoldingLayer) {
            return true
        }

        if (!this.targetField) {
            return false
        }

        if ((!this.targetCropping) && this.isCroppingLayer && (!type.CroppingOptional)) {
            return false
        }

        switch (type.TypeId) {

            case LayerTypeId.GenericPlan:
                const plan = this.genericPlanContext
                return plan && plan.isValid()

            default:
                return true

        }
    }

    canEditFeatures() {
        return (!this.readOnly) && this._canEditFeatures && this.layer && this.layer.canEditFeatures()
    }

    showCopyCmd() {
        return !this.isPictureLayer
    }

    showGridCreateCmd() {
        return !(this.isPictureLayer || this.isHoldingLayer)
    }

    isLayerEmpty(): boolean {
        return !this.zones.length
    }

    canDeleteLayer(): boolean {

        if (this.readOnly || this.newLayer) {
            return false
        }

        return canDeleteLayer(this.target)

    }

    showZoneSelector() {

        if (!this.target) return false

        switch (this.target.TypeId) {

            case LayerTypeId.Biomass:
            case LayerTypeId.Yield:
                return this.newLayer;

            default:
                return true;

        }
    }

    get valueLabel(): string {
        return this.layer.valueLabel;
    }

    get valueUnits(): string {
        return this.layer.valueUnits;
    }

    get opacity() {
        return this._opacity;
    }

    set opacity(value: number) {
        this._opacity = value;
        if (this.layer) {
            this.layer.opacity = value;
            this.refreshOnOpacityChange();
        }
    }

    private refreshOnOpacityChange() {

        if (!!this._opacityRefresh) return;

        this._opacityRefresh = this.shell.timeout(50, false).then(p => {
            this._opacityRefresh = null;
            this.map.refresh();
        });

    }

    canReset() {
        if (!this.layer) return false
        if (!this.editContext) return false
        if (this.editContext.newLayer && (!this.editContext.newLayerInit)) return false
        if (this.resetMode) return false
        if (this.isPictureLayer) return false
        if (this.isDrawingLayer) return false
        return (this.zones || []).every(z => isUndefinedOrNull(z.value) || (z.value === 0))
    }

    setResetMode(reset: boolean = true) {
        this.resetMode = reset
        this.layer.visible = !reset
        this.map.clearSelection()
        this.updateCreateContext()
    }

    canSaveLayer(): boolean {
        if (this.readOnly) return false
        if (!this.target) return false;
        if (!this.target.Type) return false;
        if (!this.layer) return false;
        if (!this.zoneEditor) return false;
        if (!this.zoneEditor.canSave()) return false;
        return this.canCommitCurrent();
    }

    private cancelCurrent(resetCurrent?: boolean) {

        if (this.zoneEditor) {
            this.zoneEditor.cancelCurrent(resetCurrent);
        }

    }

    private validate(): boolean {

        if (!this.target.Type) return false;
        if (!this.commitCurrent()) return false;

        if (this.headerForm && this.headerForm.$invalid) {
            this.showValidationErrors = true;
            return false;
        }

        this.validationResults = this.editContext.validate();

        if (this.validationResults) {
            this.showValidationErrors = true;
            return false;
        }

        if (this.zoneEditor) {
            if (!this.zoneEditor.validateLayer()) {
                return false;
            }
        }

        return true;

    }

    saveLayer() {

        if (!this.validate()) {
            return
        }

        this.showValidationErrors = false

        if (!this.isHoldingLayer) {
            this.target.FieldId = this.targetField.field.FieldId
        } else {
            this.target.HoldingId = this.holding.HoldingId
        }

        const model = getUpdateModel({
            session: this.shell.getSession(),
            layer: this.layer,
            target: this.target,
            cropping: this.targetCropping,
            plan: this.genericPlanContext
        })

        this.showGenericPlanWarning().then(() => this.saveChanges(model))

    }

    protected saveChanges(target: IDatalayerEditModel): ng.IPromise<any> {

        const { svc, config } = this

        let save = config.saveChanges

        if (!save) {

            save = m => svc.editLayer(m)
                .then(layer => {
                    this.svc.setLayerVisibility(layer.DataLayerId, true)
                    return layer
                })

        }

        return save(target)
            .then(layer => this.cancel(layer))
            .catch(r => handleApiError(this.shell, r, { resourceType: "layer" }))

    }

    showGenericPlanWarning(): ng.IPromise<any> {

        if (!this.isGenericPlanLayer) {
            return omniaResolve()
        }

        const msg = `
<p>This plan type is not linked to the NVZ rules or the nutritional models. For fertiliser applications, please use the Nutrition module.</p>
<p>Crop protection plans should only be used following recommendation from a BASIS qualified adviser.</p>`

        const title = "Generic plan warning"

        return shell.showWarning(
            this.shell,
            msg,
            title
        )

    }

    showDeleteLayer(): void {

        const msg = "<p>Click 'Delete' to delete this layer and all its associated zones.</p><p>Please note that this operation is irreversible, and that layers that have already been used as inputs to calculate other layers cannot be deleted.</p>";

        const title = "Delete this layer"

        shell.showDeleteCancel(this.shell, msg, title).then(r => {
            if (r) {
                this.promptAnalysisDeletion();
            }
        });

    }

    promptAnalysisDeletion() {

        let prompt = this.shell.resolve(false);

        switch (this.layerType.TypeId) {

            case LayerTypeId.SoilAnalysis:
            case LayerTypeId.PcnAnalysis:

                if (this.target.SubtypeId == m.SoilAnalysisLayerSubtype.GridSamples) {

                    const msg = "<p>This layer was imported from a grid samples file.</p><p>Do you want to delete all the associated analysis?</p>";
                    const title = "Delete associated analysis";

                    const msgKeepAnalysis = "No, keep the analysis";
                    const msgDeleteAnalysis = "<i class='icon icon-delete'></i>&nbsp;Yes, delete the analysis";

                    prompt = this.shell.showMessageBox({

                        content: msg,
                        title,

                        buttons: [
                            { text: str.Cancel, value: null, cssClass: "btn-warning pull-left" },
                            { text: msgDeleteAnalysis, value: true, cssClass: "btn-danger" },
                            { text: msgKeepAnalysis, value: false, cssClass: "btn-primary" }
                        ]

                    });

                }

                break;

        }

        prompt.then(r => {
            if (!isUndefinedOrNull(r)) {
                this.deleteLayer(r)
            }
        });

    }


    deleteLayer(deleteAnalysis: boolean): void {

        const layerId = this.target.DataLayerId

        this.svc.canDeleteLayer(layerId).then(r => {

            if (r.Plans && r.Plans.length) {

                const plans = r.Plans.map(p => "<li>- " + p.Name + " (" + p.Year + ")</li>").join("")

                const msg = `This layer cannot be deleted because it is used in the following plans:<ul>${plans}</ul>`

                shell.showWarning(this.shell, msg, "This layer could not be deleted", str.Ok, false)

            } else {

                const msgDeleteError = "This layer could not be deleted. Please check that it has not already been used to calculate a seeds plan or a nutrition plan.";

                this.svc.deleteLayers([layerId], deleteAnalysis)
                    .then(r => this.cancel())
                    .catch(r => handleApiError(this.shell, r, { deleting: true, resourceType: "layer", defaultMessage: msgDeleteError }))
            }

        });

    }

    cancel(result?: any) {

        if (this.target && this.target.DataLayerId) {
            this.svc.setLayerVisibility(this.target.DataLayerId, true);
        }

        this.cancelCurrent(true)
        this.cleanupLayers()
        this.cleanupEditor()

        this.closeComponent(result)

    }


    commitCurrent(): boolean {

        if (!this.canCommitCurrent()) return false;
        if (!this.zoneEditor) return false;

        return this.zoneEditor.commitCurrent();

    }


    canCommitCurrent(): boolean {

        if (this.zoneEditor) {
            return this.zoneEditor.canCommitCurrent();
        }

    }

    private cleanupLayers() {

        let map = this.map;

        this.removeOuterGeometryLayer();

        map.resetDefaultLayers();

        this.allLayers.forEach(l => l.editMode = LayerEditMode.None);

        map.croppingLayer.clearHighlight();
        map.fieldsLayer.clearHighlight();

        map.synchronizeSelectionFromMap = true;
        map.clearSelection();

        if (this.zoneEditor) {
            this.zoneEditor.dispose();
            this.zoneEditor = null
        }

        map.editLayer = null;

        if (this._planWatcher) {
            this._planWatcher()
            this._planWatcher = null
        }

        map.refresh();

    }

    private setupLayers(zoom: boolean): boolean {

        if (this.layer) {
            this.cleanupLayers()
            this.layer = null
        }

        this.map.resetDefaultLayers();

        this.map.synchronizeSelectionFromMap = false;

        this.setVisibleLayers()

        const caches = {}

        const layerContext: IDatalayerContext = {

            createMapLayerInstance: (layer, options) => this.layerFactory.create(layer, layerContext, options),

            getAllLayers: () => this.sourceLayers.filter(l => !!l.mapLayer).map(l => l.mapLayer),

            getAllFields: () => this.svc.fields,

            loader: this.svc,
            viewManager: this.viewManager,
            measuringService: this.dependencies.measuringService,
            sharedData: this.svc.sharedData,

            getCache(id: string) {
                const cache = caches[id] || (caches[id] = {})
                return cache
            },

            onZoneValueChanged: null

        };

        if (this.isPictureLayer) {
            return true
        }

        this.setupOuterGeometryLayer();

        if (this.hasType()) {

            this.setupLayerEditor(layerContext)

            if (zoom) {
                this.zoomLayer()
            }

        }

        this.map.refresh();

        return true;

    }


    protected removeOuterGeometryLayer() {

        let olmap = this.map.getMap();

        if (this._outerGeometryLayer) {
            olmap.removeLayer(this._outerGeometryLayer);
        }

    }

    protected setupOuterGeometryLayer() {

        this.removeOuterGeometryLayer();

        if (this._outerGeometry) {

            let olmap = this.map.getMap();

            let points = omnia.map.getGeometryPoints(this._outerGeometry, false);
            let pointsFeature = new ol_Feature(points);

            let source = new ol_source.Vector({
                features: [pointsFeature]
            });

            this._outerGeometryLayer = new ol_layer.Vector({

                source: source,

                style: [

                    new ol_style.Style({

                        image: new ol_style.Circle({
                            fill: new ol_style.Fill({ color: 'rgba(255, 255, 255, 0.5)' }),
                            stroke: new ol_style.Stroke({ width: 2, color: '#000000' }),
                            radius: 4,
                        })

                    }),

                ]


            });

            olmap.addLayer(this._outerGeometryLayer);

        }

    }

    protected setVisibleLayers() {

        let visible = this.getVisibleLayers()

        if (visible) {

            const show = !this.isHoldingLayer

            visible.forEach(l => {
                l.visible = show
                l.editMode = LayerEditMode.InEditor
            })

        }

        if (this.isGenericPlanLayer) {

            this._planWatcher = this.$scope.$watch("vm.genericPlanContext.id", (id: number) => {

                if (this.allLayers && this.layer) {

                    const layers = this.allLayers
                        .filter(l => l.checkTypeId(LayerTypeId.GenericPlan) && (l.datalayerId != this.layer.datalayerId))

                    layers.forEach(l => l.visible = (l.datalayer.GenericPlanId == id))


                }

            })

        }

    }

    protected getVisibleLayers() {

        var typeId = this.target.TypeId;

        switch (typeId) {

            case LayerTypeId.SoilType:
            case LayerTypeId.SubsoilType:
                // 200927 - Usersnap 1763
                // Added the (l.field) filter - in effect, this excludes layers that are associated
                // with disabled fields
                return this.allLayers.filter(l => l.field && l.checkTypeId(typeId))

            default:
                return this.visibleLayers

        }

    }


    private zoomLayer() {

        let zones = true

        if (this.layerType && (!this.isHoldingLayer) && (this.layerType.TypeId == LayerTypeId.Drawing)) {
            zones = false
        }

        if (zones && this.layer.zones.length) {
            this.map.viewExtent(this.layer.getExtent())
            return
        }

        const targetFeature = this.editContext.getContainingFeature();

        if (targetFeature) {
            this.map.viewExtent(targetFeature.getGeometry());
        }

    }

    private onZonesChanged(zones: DatalayerZone[]) {
        if (this.genericPlanContext) {
            this.genericPlanContext.updateTotals()
        }
    }

}
function hasAuthorizedCroppingOnField(targetField: FieldViewModel) {
    if (!targetField) {
        return true
    }
    return targetField.croppings?.some(c => c.currentYear && c.authorized)
}

